import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Button, Card, CardBody } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import { useAccountCustomerPayments, useAccountCustomerPayment } from "hooks/useAccountCustomerPayment"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import AccountCustomerPaymentAddEdit from "pages/AccountPages/AccountCustomerPayment/AccountCustomerPaymentAddEdit"
import AccountCustomerPaymentView from "pages/AccountPages/AccountCustomerPayment/AccountCustomerPaymentView"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getActiveRecords, getGrossAmount, getReversedAmount, getReversedRecords, getNetAmount } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const ReceivablesTab = ({ permissionFlags }) => {
  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)

  const { data: paymentResponse, isLoading } = useAccountCustomerPayments(
    { page: 1, per_page: 8 },
    permissionFlags.canViewCustomerPayments
  )
  const { data: viewResponse, isLoading: viewLoading } = useAccountCustomerPayment(selectedViewId, viewModal)
  const payments = paymentResponse?.data?.data || []
  const visiblePayments = useMemo(() => getActiveRecords(payments), [payments])
  const reversedPayments = useMemo(() => getReversedRecords(payments), [payments])
  const grossAmount = useMemo(() => getGrossAmount(payments), [payments])
  const reversedAmount = useMemo(() => getReversedAmount(payments), [payments])

  const totalAmount = useMemo(
    () => getNetAmount(payments),
    [payments]
  )

  const columns = useMemo(() => [
    { header: "Customer", cell: ({ row }) => row.original.customer?.name || "-" },
    { header: "Method", cell: ({ row }) => row.original.payment_method?.name || row.original.paymentMethod?.name || "-" },
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.received_date ? formatReadableDate(row.original.received_date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, row.original.account?.currency || "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    {
      header: "Action",
      cell: ({ row }) => (
        <Button size="sm" color="primary" onClick={() => { setSelectedViewId(row.original.id); setViewModal(true) }}>
          View
        </Button>
      ),
    },
  ], [])

  return (
    <div className="d-flex flex-column gap-4">
      <div className="row g-3">
        <div className="col-md-4">
          <WorkspaceStatCard title="Gross Receipts" value={formatCurrency(grossAmount, "BDT")} subtitle={`${payments.length} visible receipt record(s)`} icon="mdi mdi-cash-multiple" color="secondary" />
        </div>
        <div className="col-md-4">
          <WorkspaceStatCard title="Reversed Receipts" value={formatCurrency(reversedAmount, "BDT")} subtitle={`${reversedPayments.length} reversed in the visible feed`} icon="mdi mdi-undo-variant" color="info" />
        </div>
        <div className="col-md-4">
          <WorkspaceStatCard title="Net Receipts" value={formatCurrency(totalAmount, "BDT")} subtitle={`${visiblePayments.length} active customer payment(s)`} icon="mdi mdi-chart-line" color="primary" />
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1">Receivables</h5>
              <p className="text-muted mb-0 small">Track incoming customer payments without leaving the finance workspace.</p>
            </div>
            <div className="d-flex gap-2">
              {permissionFlags.canAddCustomerPayment && (
                <Button color="primary" onClick={() => { setSelectedData(null); setModal(true) }}>
                  <i className="mdi mdi-cash-plus me-1" />
                  Add Payment
                </Button>
              )}
              <Link to="/account-customer-payment-list" className="btn btn-light border">Open full page</Link>
            </div>
          </div>

          <TableContainer columns={columns} data={payments} loading={isLoading} isPagination={false} />
        </CardBody>
      </Card>

      <AccountCustomerPaymentAddEdit modal={modal} isEdit={false} editedData={selectedData} toggleModal={() => { setModal(prev => !prev); if (modal) setSelectedData(null) }} />
      <AccountCustomerPaymentView modal={viewModal} toggleModal={() => { setViewModal(prev => !prev); if (viewModal) setSelectedViewId(null) }} payment={viewResponse?.data} loading={viewLoading} />
    </div>
  )
}

export default ReceivablesTab
